import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  ENGINEERING_BREAKS,
  getEngineeringBreakById,
} from '../data/breaks-demo-dataset';
import type { EngineeringBreak } from '../types/breaks';
import { BreakCard } from './BreakCard';
import { BreakDetailDrawer } from './BreakDetailDrawer';
import { SEVERITY_LINE, SEVERITY_LINE_WIDTH } from './break-styles';

/**
 * One break placed on the ring that orbits the Intera hub. Each slot carries
 * its absolute md+ placement (anchored on its own center via a -50%/-50%
 * translate) plus the point - in the 0–100 viewBox space of the connector
 * overlay - that the hub draws a line to.
 */
interface RingSlot {
  id: string;
  /** Absolute placement at md+ (mobile stacks in a single column). */
  positionClass: string;
  widthClass: string;
  /** Resting tilt at md+. */
  rotationClass: string;
  zClass: string;
  delayMs: number;
  /** Connector endpoint in the overlay's 0–100 coordinate space. */
  point: { x: number; y: number };
}

/**
 * Six high-urgency breaks arranged in a ring around the hub - the manager
 * should feel encircled by gaps that need a decision now. The remaining breaks
 * live behind "Continue".
 */
const RING: RingSlot[] = [
  {
    id: 'data-pipeline-reestimated',
    positionClass:
      'md:start-[17%] md:top-[14%] md:-translate-x-1/2 md:-translate-y-1/2 rtl:md:translate-x-1/2',
    widthClass: 'md:max-w-[330px]',
    rotationClass: 'md:-rotate-2',
    zClass: 'z-20',
    delayMs: 150,
    point: { x: 17, y: 14 },
  },
  {
    id: 'checkout-epic-blocked',
    positionClass:
      'md:start-[83%] md:top-[14%] md:-translate-x-1/2 md:-translate-y-1/2 rtl:md:translate-x-1/2',
    widthClass: 'md:max-w-[330px]',
    rotationClass: 'md:rotate-2',
    zClass: 'z-20',
    delayMs: 300,
    point: { x: 83, y: 14 },
  },
  {
    id: 'add-driver-prd-missing-collective',
    positionClass:
      'md:start-[92%] md:top-[52%] md:-translate-x-1/2 md:-translate-y-1/2 rtl:md:translate-x-1/2',
    widthClass: 'md:max-w-[300px]',
    rotationClass: 'md:rotate-3',
    zClass: 'z-10',
    delayMs: 450,
    point: { x: 92, y: 52 },
  },
  {
    id: 'stale-dependency-incident',
    positionClass:
      'md:start-[81%] md:top-[85%] md:-translate-x-1/2 md:-translate-y-1/2 rtl:md:translate-x-1/2',
    widthClass: 'md:max-w-[330px]',
    rotationClass: 'md:-rotate-3',
    zClass: 'z-20',
    delayMs: 600,
    point: { x: 81, y: 85 },
  },
  {
    id: 'gtm-shift-sprint-unchanged',
    positionClass:
      'md:start-[19%] md:top-[85%] md:-translate-x-1/2 md:-translate-y-1/2 rtl:md:translate-x-1/2',
    widthClass: 'md:max-w-[330px]',
    rotationClass: 'md:rotate-3',
    zClass: 'z-20',
    delayMs: 750,
    point: { x: 19, y: 85 },
  },
  {
    id: 'prd-employer-number-pathway',
    positionClass:
      'md:start-[8%] md:top-[52%] md:-translate-x-1/2 md:-translate-y-1/2 rtl:md:translate-x-1/2',
    widthClass: 'md:max-w-[300px]',
    rotationClass: 'md:-rotate-3',
    zClass: 'z-10',
    delayMs: 900,
    point: { x: 8, y: 52 },
  },
];

const criticalCount = ENGINEERING_BREAKS.filter(
  (engineeringBreak) => engineeringBreak.severity === 'high'
).length;

interface BreaksGalleryPageProps {
  /**
   * Called when the user advances from the gallery. Supplied when the gallery
   * runs as an onboarding step (it then finishes onboarding); omitted on the
   * standalone /breaks route, where it just routes into the app.
   */
  onContinue?: () => void;
  /** True while onboarding completion is in flight (disables the CTA). */
  isFinishing?: boolean;
}

export function BreaksGalleryPage({
  onContinue,
  isFinishing,
}: BreaksGalleryPageProps = {}) {
  const navigate = useNavigate();
  const [selectedBreakId, setSelectedBreakId] = useState<string | null>(null);

  const selectedBreak: EngineeringBreak | null =
    (selectedBreakId && getEngineeringBreakById(selectedBreakId)) || null;

  const handleContinue = () => {
    if (onContinue) {
      onContinue();
      return;
    }
    navigate({ to: '/command-center' });
  };

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-white pb-28 text-black">
      <style>{HUB_KEYFRAMES}</style>

      {/* Ethereal brand light */}
      <div className="pointer-events-none absolute -start-32 -top-40 h-[760px] w-[760px] rounded-full bg-[#00AEEF] opacity-[0.10] blur-[150px]" />
      <div className="pointer-events-none absolute -end-32 top-1/3 h-[680px] w-[680px] rounded-full bg-[#1E3A8A] opacity-[0.08] blur-[150px]" />

      <div className="relative z-10 mx-auto w-full max-w-[1200px] px-5 pt-20 md:px-8 md:pt-24">
        {/* Hero - urgency forward */}
        <header className="scatter-enter mx-auto mb-12 max-w-3xl text-center md:mb-16">
          <div className="mb-7 inline-flex items-center gap-2 rounded-full bg-rose-50 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-rose-600 ring-1 ring-inset ring-rose-200">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-500" />
            </span>
            {ENGINEERING_BREAKS.length} open gaps · {criticalCount} critical
          </div>
          <h1 className="mb-6 text-4xl font-light leading-[1.1] tracking-tight text-black sm:text-5xl md:text-6xl">
            Here&apos;s what Intera caught across Engineering.
          </h1>
          <p className="mx-auto max-w-xl text-base font-light leading-relaxed text-zinc-500 md:text-lg">
            Every one is an open execution gap - surfaced from your connected
            tools and waiting on a decision.{' '}
            <span className="font-normal text-rose-600">
              The red ones won&apos;t wait.
            </span>
          </p>
        </header>

        {/* Hub-and-spoke: Intera at the center, the gaps orbiting it */}
        <main className="relative flex w-full flex-col items-center gap-6 md:block md:h-[920px] md:gap-0">
          {/* Connector lines drawn from the hub out to each card (md+ only) */}
          <svg
            className="pointer-events-none absolute inset-0 hidden h-full w-full md:block"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            {RING.map((slot) => {
              const engineeringBreak = getEngineeringBreakById(slot.id);
              if (!engineeringBreak) return null;
              return (
                <line
                  key={slot.id}
                  x1="50"
                  y1="50"
                  x2={slot.point.x}
                  y2={slot.point.y}
                  stroke={SEVERITY_LINE[engineeringBreak.severity]}
                  strokeWidth={SEVERITY_LINE_WIDTH[engineeringBreak.severity]}
                  strokeDasharray="4 5"
                  vectorEffect="non-scaling-stroke"
                  className="spoke-flow"
                />
              );
            })}
          </svg>

          {/* The Intera hub - in-flow at the top on mobile, centered at md+.
              Outer holds placement + centering translate; inner runs the reveal
              animation so its scale transform never clobbers the centering. */}
          <div className="relative z-30 mb-2 md:absolute md:start-1/2 md:top-1/2 md:mb-0 md:-translate-x-1/2 md:-translate-y-1/2 rtl:md:translate-x-1/2">
            <div className="hub-enter relative flex h-44 w-44 items-center justify-center md:h-52 md:w-52">
              {/* Radar pulses - Intera is actively watching */}
              <span className="absolute inset-0 rounded-full bg-cyan-400/10 animate-ping [animation-duration:3s]" />
              <span className="absolute inset-4 rounded-full bg-cyan-400/10 animate-ping [animation-duration:3s] [animation-delay:1s]" />
              <span className="absolute inset-0 rounded-full ring-1 ring-cyan-200/60" />
              <span className="absolute inset-6 rounded-full ring-1 ring-cyan-100" />
              <div className="relative flex h-32 w-32 flex-col items-center justify-center gap-2 rounded-full border border-zinc-100 bg-white shadow-[0_20px_50px_-15px_rgba(0,174,239,0.45)] md:h-36 md:w-36">
                <img
                  src="/intera_logo_blue.svg"
                  alt="Intera"
                  className="h-11 w-auto md:h-12"
                />
                <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-zinc-800">
                  Intera
                </span>
              </div>
            </div>
          </div>

          {/* The orbiting breaks */}
          {RING.map((slot) => {
            const engineeringBreak = getEngineeringBreakById(slot.id);
            if (!engineeringBreak) return null;
            return (
              // Outer holds ring placement + centering translate; inner runs the
              // entrance animation so its transform never clobbers positioning.
              <div
                key={slot.id}
                className={`relative w-full max-w-[340px] md:absolute ${slot.positionClass} ${slot.widthClass} ${slot.zClass}`}
              >
                <div
                  className="scatter-enter"
                  style={{ animationDelay: `${slot.delayMs}ms` }}
                >
                  <BreakCard
                    engineeringBreak={engineeringBreak}
                    onOpen={setSelectedBreakId}
                    rotationClass={slot.rotationClass}
                  />
                </div>
              </div>
            );
          })}
        </main>

        {/* Quiet way forward */}
        <div className="scatter-enter mt-16 flex flex-col items-center gap-4 text-center md:mt-24">
          <p className="text-sm font-light text-zinc-400">
            …and {ENGINEERING_BREAKS.length - RING.length} more gaps waiting
            inside.
          </p>
          <button
            type="button"
            onClick={handleContinue}
            disabled={isFinishing}
            className="rounded-full border border-button-primary bg-white px-7 py-2.5 text-sm font-semibold text-button-primary transition-colors hover:bg-button-primary hover:text-white active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Continue to Intera
          </button>
        </div>
      </div>

      <BreakDetailDrawer
        engineeringBreak={selectedBreak}
        onClose={() => setSelectedBreakId(null)}
      />
    </div>
  );
}

const HUB_KEYFRAMES = `
@keyframes scatterFloatUp {
  0% { opacity: 0; transform: translateY(40px) scale(0.96); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}
.scatter-enter {
  animation: scatterFloatUp 1s cubic-bezier(0.16, 1, 0.3, 1) both;
}
@keyframes hubReveal {
  0% { opacity: 0; transform: scale(0.8); }
  100% { opacity: 1; transform: scale(1); }
}
.hub-enter {
  animation: hubReveal 1.1s cubic-bezier(0.16, 1, 0.3, 1) both;
}
@keyframes spokeFlow {
  to { stroke-dashoffset: -18; }
}
.spoke-flow {
  animation: spokeFlow 1.2s linear infinite;
}
@media (prefers-reduced-motion: reduce) {
  .scatter-enter, .hub-enter, .spoke-flow { animation: none; }
}
`;
